/**
 * Forgot password (Figma 10 · Forgot password 01–06). Ask → sent (the same
 * words whether or not the address has an account) → from the link: set a new
 * password → done, signed in, the other devices signed out. A link that ended
 * or was already used says so, with "Email a new link".
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { CircleCheck, Hourglass, Link2Off, Mail, WifiOff } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { PublicShell } from "@/components/gettingIn/PublicShell";
import {
  Column,
  GiButton,
  GiField,
  Head,
  Note,
  Notice,
  QuietAction,
  StatusHead,
  Ways,
} from "@/components/gettingIn/parts";
import { usePasswordRule } from "@/components/gettingIn/usePasswordRule";
import { useResendClock } from "@/components/gettingIn/useResendClock";
import { EMAIL_PATTERN, authCalls, readAuthFailure } from "@/components/gettingIn/authApi";

const SIGN_IN = <QuietAction to="/login">Sign in</QuietAction>;

export default function ForgotPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  return token ? <ResetFromLink token={token} /> : <AskForLink initialEmail={params.get("email") ?? ""} />;
}

/* ------------------------------------------------------------ 01 / 02 */

function AskForLink({ initialEmail, sentTo }: { initialEmail: string; sentTo?: string }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState(initialEmail);
  const [sent, setSent] = useState<string | null>(sentTo ?? null);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  const [sending, setSending] = useState(false);
  const clock = useResendClock();

  useEffect(() => {
    if (sentTo) clock.start();
    // Only on arrival with an address already sent to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async (address: string) => {
    if (!EMAIL_PATTERN.test(address.trim())) return setError("That does not look like an email address.");
    setSending(true);
    setError(null);
    setOffline(false);
    try {
      await authCalls.forgot(address.trim());
      setSent(address.trim());
      clock.start();
    } catch (e) {
      if (readAuthFailure(e).kind === "offline") setOffline(true);
      else setError("That address did not work. Check it and try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <PublicShell still="network" title="Check your inbox" topAction={SIGN_IN}>
        <Column>
          <StatusHead icon={Mail} title="Check your inbox">
            If an account uses {sent}, a link is on its way. It works once, for 30 minutes.
          </StatusHead>
          {offline ? (
            <Notice tone="attention" icon={WifiOff}>
              Creo cannot be reached. Check your connection and resend.
            </Notice>
          ) : null}
          <Ways>
            <GiButton
              variant="secondary"
              onClick={() => void send(sent)}
              loading={sending}
              disabled={clock.waiting}
              className={clock.waiting ? "!opacity-40" : undefined}
            >
              {clock.waiting ? `Resend in ${clock.label}` : "Resend link"}
            </GiButton>
          </Ways>
          <Note>Nothing after a few minutes? Look in spam, or check the address.</Note>
          <QuietAction
            onClick={() => {
              setEmail(sent);
              setSent(null);
            }}
          >
            Use another address
          </QuietAction>
        </Column>
      </PublicShell>
    );
  }

  return (
    <PublicShell still="network" title="Reset your password" topAction={SIGN_IN}>
      <Column>
        <Head title="Reset your password" sub="We email you a link. It works once, for 30 minutes." />
        {offline ? (
          <Notice tone="attention" icon={WifiOff}>
            Creo cannot be reached. Check your connection; what you typed stays.
          </Notice>
        ) : null}
        <form
          className="flex w-full flex-col gap-6"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void send(email);
          }}
        >
          <GiField
            label="Email"
            type="email"
            autoComplete="email"
            placeholder="you@studio.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={error}
            readOnly={sending}
          />
          <GiButton type="submit" loading={sending}>
            {offline ? "Try again" : "Email me a link"}
          </GiButton>
        </form>
        <QuietAction onClick={() => navigate("/login")}>Back to sign in</QuietAction>
      </Column>
    </PublicShell>
  );
}

/* ------------------------------------------------------------ 03 – 06 */

type LinkState =
  | { kind: "checking" }
  | { kind: "ready"; email: string }
  | { kind: "ended" | "used"; email: string | null }
  | { kind: "invalid" }
  | { kind: "offline" }
  | { kind: "done" }
  | { kind: "resent"; email: string };

function ResetFromLink({ token }: { token: string }) {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [state, setState] = useState<LinkState>({ kind: "checking" });
  const password = usePasswordRule();
  const [saving, setSaving] = useState(false);
  const [saveOffline, setSaveOffline] = useState(false);
  // The server refused the password itself (on a compromised list, or the old
  // one on a locked account): said on the field, cleared by typing.
  const [refused, setRefused] = useState<string | null>(null);
  const inspected = useRef(false);

  const readLink = (error: unknown): LinkState => {
    const failure = readAuthFailure(error);
    if (failure.kind !== "coded") return { kind: "offline" };
    const email = typeof failure.data.email === "string" ? failure.data.email : null;
    if (failure.code === "LINK_ENDED") return { kind: "ended", email };
    if (failure.code === "LINK_USED") return { kind: "used", email };
    return { kind: "invalid" };
  };

  useEffect(() => {
    if (inspected.current) return;
    inspected.current = true;
    authCalls
      .inspectReset(token)
      .then(({ email }) => setState({ kind: "ready", email }))
      .catch((error) => setState(readLink(error)));
  }, [token]);

  const save = async () => {
    if (!password.check()) return;
    setSaving(true);
    setSaveOffline(false);
    try {
      const session = await authCalls.reset(token, password.value);
      login(session.token, session.refreshToken ?? "");
      setState({ kind: "done" });
    } catch (error) {
      const failure = readAuthFailure(error);
      if (failure.kind === "coded" && failure.code === "PASSWORD_COMPROMISED") {
        setRefused(
          failure.data.source === "common_list"
            ? "This is one of the most used passwords. Choose a different one."
            : "This password has appeared in a data breach. Choose a different one.",
        );
        return;
      }
      if (failure.kind === "coded" && failure.code === "PASSWORD_REUSED") {
        setRefused("Choose a password you have not used on this account before.");
        return;
      }
      const next = readLink(error);
      if (next.kind === "offline") setSaveOffline(true);
      else setState(next);
    } finally {
      setSaving(false);
    }
  };

  if (state.kind === "resent") return <AskForLink initialEmail={state.email} sentTo={state.email} />;

  const newLink = (email: string | null) => (
    <Ways>
      <GiButton
        onClick={() =>
          email
            ? void authCalls.forgot(email).then(() => setState({ kind: "resent", email }))
            : navigate("/forgot-password", { replace: true })
        }
      >
        Email a new link
      </GiButton>
      <GiButton variant="secondary" onClick={() => navigate("/login")}>
        Sign in
      </GiButton>
    </Ways>
  );

  return (
    <PublicShell still="network" title="Choose a new password" topAction={state.kind === "done" ? undefined : SIGN_IN}>
      <Column>
        {state.kind === "checking" ? (
          <StatusHead icon={Mail} title="Opening your link" />
        ) : state.kind === "ready" ? (
          <form
            className="flex w-full flex-col gap-6"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <Head title="Choose a new password" sub={`For ${state.email}.`} align="start" />
            {saveOffline ? (
              <Notice tone="attention" icon={WifiOff}>
                Creo cannot be reached. Check your connection; what you typed stays.
              </Notice>
            ) : null}
            {/* Lets a password manager save the pair. */}
            <input type="email" value={state.email} autoComplete="username" readOnly hidden />
            <GiField
              label="New password"
              secret
              autoComplete="new-password"
              placeholder="Choose a password"
              hint={password.hint}
              error={password.error ?? refused}
              readOnly={saving}
              autoFocus
              {...password.field}
              onChange={(e) => {
                password.field.onChange(e);
                setRefused(null);
              }}
            />
            <Note className="!text-left">Saving signs you out on your other devices.</Note>
            <GiButton type="submit" loading={saving}>
              {saveOffline ? "Try again" : "Save password"}
            </GiButton>
          </form>
        ) : state.kind === "done" ? (
          <>
            <StatusHead icon={CircleCheck} title="Password changed">
              You are signed in. Your other devices were signed out.
            </StatusHead>
            <Ways>
              <GiButton onClick={() => navigate("/dashboard", { replace: true })}>Open Creo</GiButton>
            </Ways>
          </>
        ) : state.kind === "ended" ? (
          <>
            <StatusHead icon={Hourglass} title="This link ended">
              Reset links last 30 minutes.{" "}
              {state.email ? `A new one goes to ${state.email}.` : "Ask for a new one."}
            </StatusHead>
            {newLink(state.email)}
          </>
        ) : state.kind === "used" ? (
          <>
            <StatusHead icon={Link2Off} title="This link was already used">
              Each link works once. If you did not change your password, ask for a new link now.
            </StatusHead>
            {newLink(state.email)}
          </>
        ) : state.kind === "offline" ? (
          <>
            <StatusHead icon={WifiOff} title="Creo cannot be reached">
              Your connection may be down, or we are. Open the link again in a moment.
            </StatusHead>
            <Ways>
              <GiButton onClick={() => window.location.reload()}>Try again</GiButton>
            </Ways>
          </>
        ) : (
          <>
            <StatusHead icon={Link2Off} title="This link does not open anything">
              It may have been copied only in part. Ask for a new one.
            </StatusHead>
            {newLink(null)}
          </>
        )}
      </Column>
    </PublicShell>
  );
}
